/**
 * The demo automation engine: workflows generated one-for-one from a tenant
 * fixture's `automations` list, so a workflow's status always agrees with the
 * Overview's "Automations running" KPI (`generators/overview.ts`), which
 * counts the same fixtures. Everything downstream — conditions, actions,
 * scheduled actions and activity — is a pure function of (tenant seed,
 * automation id), so it is stable across screens, reloads and tests, and
 * activity/scheduled actions reference real demo competitions and campaigns
 * rather than invented ones.
 */

import {
  actionDefinition,
  triggerDefinition,
  type ActionKey,
  type ActivityEntry,
  type ActivityOutcome,
  type ScheduledAction,
  type ScheduledActionState,
  type TriggerFamily,
  type TriggerKey,
  type Workflow,
  type WorkflowAction,
  type WorkflowCondition,
  type WorkflowTrigger,
} from '@/api/automation'
import type { EntityRef } from '@/lib/drilldown'
import { addDays } from '@/lib/filters'

import { unitNoise } from '../random'
import type { AutomationFixture, TenantFixture } from '../tenants/types'

function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function windowFor(lower: string, num: string): string {
  if (/week|monday|tuesday|wednesday|thursday|friday|saturday|sunday/.test(lower)) return 'weekly'
  if (/month/.test(lower)) return 'monthly'
  if (/hour/.test(lower)) return `${num || '1'}h`
  if (/day/.test(lower)) return `${num || '1'}d`
  return '24h'
}

/** Best-effort classification of a fixture's plain-English trigger into the catalogue. */
export function inferTrigger(fixture: AutomationFixture): WorkflowTrigger {
  const text = fixture.trigger
  const lower = text.toLowerCase()
  const num = text.match(/\d+/)?.[0] ?? ''
  let key: TriggerKey
  const params: Record<string, string> = {}

  if (lower.includes('abandoned')) {
    key = 'marketing_basket_abandoned'
    params.hours = num || '1'
  } else if (lower.includes('sold') && lower.includes('%')) {
    key = 'competition_pct_sold'
    params.percent = num || '80'
  } else if (lower.includes('pace')) {
    key = 'competition_behind_pace'
  } else if (lower.includes('hours to close') || lower.includes('hours before')) {
    key = 'competition_hours_to_close'
    params.hours = num || '24'
  } else if (lower.includes('sold out')) {
    key = 'competition_sold_out'
  } else if (lower.includes('ends') || (lower.includes('closes') && !lower.includes('hours'))) {
    key = 'competition_ended'
  } else if (lower.includes('published')) {
    key = 'competition_published'
  } else if (lower.includes('competitor') || lower.includes('similar prize')) {
    key = 'market_competitor_launch'
  } else if (lower.includes('price')) {
    key = 'market_price_change'
  } else if (lower.includes('campaign') && (lower.includes('budget') || lower.includes('spent'))) {
    key = 'marketing_campaign_milestone'
  } else {
    key = 'analytics_threshold'
    params.metric = slug(text)
    params.operator = num ? 'gte' : 'eq'
    params.value = num || 'true'
    params.window = windowFor(lower, num)
  }

  const definition = triggerDefinition(key)
  return { key, family: definition.family, label: definition.label, module: definition.module, detail: text, params }
}

const FAMILY_ACTIONS: Record<TriggerFamily, readonly ActionKey[]> = {
  competition: ['notify_team', 'create_alert', 'trigger_publishing'],
  analytics: ['create_alert', 'notify_team', 'generate_report'],
  market: ['create_alert', 'create_recommendation'],
  marketing: ['send_email', 'send_push', 'schedule_campaign'],
}

const ACTION_DETAILS: Record<ActionKey, string> = {
  create_alert: 'Raises an alert in Insights for review.',
  notify_team: 'Posts a notification for the team.',
  schedule_campaign: 'Schedules the linked campaign to launch.',
  generate_report: 'Runs the linked report and prepares it for export.',
  create_recommendation: 'Adds a recommendation to Insights, for approval.',
  trigger_publishing: 'Publishes the listing to its configured targets.',
  send_email: 'Emails the affected customer.',
  send_push: 'Sends a push notification to the affected customer.',
}

function buildActions(family: TriggerFamily, seed: number, workflowId: string): WorkflowAction[] {
  const pool = FAMILY_ACTIONS[family]
  const first = Math.floor(unitNoise(seed, workflowId, 'action-0') * pool.length)
  const keys = [pool[first] as ActionKey]
  if (unitNoise(seed, workflowId, 'action-count') > 0.4 && pool.length > 1) {
    const offset = 1 + Math.floor(unitNoise(seed, workflowId, 'action-1') * (pool.length - 1))
    const second = pool[(first + offset) % pool.length] as ActionKey
    if (second !== keys[0]) keys.push(second)
  }
  return keys.map((key) => ({ key, label: actionDefinition(key).label, detail: ACTION_DETAILS[key] }))
}

function buildConditions(trigger: WorkflowTrigger, tenant: TenantFixture, seed: number, workflowId: string): WorkflowCondition[] {
  const conditions: WorkflowCondition[] = []
  switch (trigger.family) {
    case 'competition': {
      const categories = [...new Set(tenant.competitions.map((competition) => competition.category))]
      const category = categories[Math.floor(unitNoise(seed, workflowId, 'cond-category') * categories.length)]
      if (category) conditions.push({ field: 'category', label: 'Competition category', operator: 'eq', value: category })
      break
    }
    case 'analytics':
      conditions.push({ field: 'window', label: 'Time window', operator: 'eq', value: trigger.params.window ?? '24h' })
      break
    case 'market':
      conditions.push({ field: 'confidence', label: 'Observation confidence', operator: 'gte', value: 'medium' })
      break
    case 'marketing': {
      const digital = tenant.channels.filter((channel) => channel.kind === 'digital')
      const channel = digital[Math.floor(unitNoise(seed, workflowId, 'cond-channel') * digital.length)]
      if (channel) conditions.push({ field: 'channel', label: 'Channel', operator: 'eq', value: channel.name })
      break
    }
  }
  if (unitNoise(seed, workflowId, 'cond-env') < 0.5) {
    conditions.push({ field: 'environment', label: 'Environment', operator: 'eq', value: 'production' })
  }
  return conditions
}

interface SubjectRef {
  entity: EntityRef | null
  path: string
}

/** The one demo entity a workflow's activity and schedule are told against — a real competition or campaign where the family suggests one. */
function subjectFor(trigger: WorkflowTrigger, tenant: TenantFixture, seed: number, workflowId: string): SubjectRef {
  if (trigger.family === 'competition' && tenant.competitions.length > 0) {
    const competition = tenant.competitions[Math.floor(unitNoise(seed, workflowId, 'subject') * tenant.competitions.length)]
    if (competition) {
      return { entity: { type: 'competition', id: competition.id, label: competition.name }, path: `/analytics/competitions/${competition.id}` }
    }
  }
  if (trigger.family === 'marketing' && tenant.campaigns.length > 0) {
    const campaign = tenant.campaigns[Math.floor(unitNoise(seed, workflowId, 'subject') * tenant.campaigns.length)]
    if (campaign) {
      return { entity: { type: 'campaign', id: campaign.id, label: campaign.name }, path: `/marketing/campaigns/${campaign.id}` }
    }
  }
  return { entity: null, path: `/automation/workflows/${workflowId}` }
}

function atTimestamp(dateKey: string, seed: number, key: string): string {
  const hour = Math.floor(unitNoise(seed, key, 'hour') * 24)
  const minute = Math.floor(unitNoise(seed, key, 'minute') * 60)
  return `${dateKey}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00.000Z`
}

export function buildWorkflow(fixture: AutomationFixture, tenant: TenantFixture, today: string): Workflow {
  const seed = tenant.seed
  const trigger = inferTrigger(fixture)
  const active = fixture.status !== 'draft'
  const runCount = active ? 5 + Math.floor(unitNoise(seed, fixture.id, 'runs') * 250) : 0
  const lastRunAt = active ? atTimestamp(addDays(today, -Math.floor(unitNoise(seed, fixture.id, 'last-run') * 6)), seed, `${fixture.id}:last`) : null
  const createdDaysAgo = 30 + Math.floor(unitNoise(seed, fixture.id, 'created') * 200)
  const createdAt = atTimestamp(addDays(today, -createdDaysAgo), seed, `${fixture.id}:created`)
  const updatedDaysAgo = Math.floor(unitNoise(seed, fixture.id, 'updated') * Math.min(21, createdDaysAgo))
  const updatedAt = atTimestamp(addDays(today, -updatedDaysAgo), seed, `${fixture.id}:updated`)

  return {
    id: fixture.id,
    name: fixture.name,
    status: fixture.status,
    trigger,
    conditions: buildConditions(trigger, tenant, seed, fixture.id),
    actions: buildActions(trigger.family, seed, fixture.id),
    lastRunAt,
    runCount,
    createdBy: tenant.user.name,
    createdAt,
    updatedAt,
  }
}

export function buildWorkflows(tenant: TenantFixture, today: string): Workflow[] {
  return tenant.automations.map((fixture) => buildWorkflow(fixture, tenant, today))
}

function scheduledState(seed: number, workflowId: string, index: number, dueAt: string, today: string): ScheduledActionState {
  const future = dueAt.slice(0, 10) >= today
  const roll = unitNoise(seed, workflowId, 'sched-state', index)
  if (future) return roll < 0.85 ? 'pending' : 'cancelled'
  return roll < 0.8 ? 'completed' : 'failed'
}

export function buildScheduledActions(tenant: TenantFixture, workflows: readonly Workflow[], today: string): ScheduledAction[] {
  const seed = tenant.seed
  const entries: ScheduledAction[] = []
  for (const workflow of workflows) {
    if (workflow.status !== 'running') continue
    const subject = subjectFor(workflow.trigger, tenant, seed, workflow.id)
    const count = 1 + Math.floor(unitNoise(seed, workflow.id, 'sched-count') * 3)
    for (let index = 0; index < count; index += 1) {
      const offsetDays = Math.floor(unitNoise(seed, workflow.id, 'sched-offset', index) * 9) - 3
      const dueAt = atTimestamp(addDays(today, offsetDays), seed, `${workflow.id}:sched:${index}`)
      const action = workflow.actions[index % workflow.actions.length] as WorkflowAction
      entries.push({
        id: `${workflow.id}:sched:${index}`,
        workflowId: workflow.id,
        workflowName: workflow.name,
        action,
        dueAt,
        state: scheduledState(seed, workflow.id, index, dueAt, today),
        entity: subject.entity,
        drillDown: { path: subject.path, ...(subject.entity ? { entity: subject.entity } : {}) },
      })
    }
  }
  return entries.sort((a, b) => a.dueAt.localeCompare(b.dueAt) || a.id.localeCompare(b.id))
}

const OUTCOME_SUMMARY: Record<ActivityOutcome, (action: WorkflowAction, triggerDetail: string) => string> = {
  success: (action, triggerDetail) => `${action.label} — ${triggerDetail}`,
  failure: (action, triggerDetail) => `${action.label} failed — ${triggerDetail}`,
  skipped: (action, triggerDetail) => `${action.label} skipped — conditions not met (${triggerDetail})`,
}

function outcomeFor(seed: number, key: string): ActivityOutcome {
  const roll = unitNoise(seed, key, 'outcome')
  if (roll < 0.82) return 'success'
  if (roll < 0.94) return 'skipped'
  return 'failure'
}

export function buildActivity(tenant: TenantFixture, workflows: readonly Workflow[], today: string): ActivityEntry[] {
  const seed = tenant.seed
  const entries: ActivityEntry[] = []
  for (const workflow of workflows) {
    if (workflow.status === 'draft' || !workflow.lastRunAt) continue
    const subject = subjectFor(workflow.trigger, tenant, seed, workflow.id)
    const count = 3 + Math.floor(unitNoise(seed, workflow.id, 'activity-count') * 10)
    for (let index = 0; index < count; index += 1) {
      const dayOffset = -Math.floor(unitNoise(seed, workflow.id, 'activity-day', index) * 14)
      const key = `${workflow.id}:act:${index}`
      const at = atTimestamp(addDays(today, dayOffset), seed, key)
      const action = workflow.actions[Math.floor(unitNoise(seed, key, 'act-action') * workflow.actions.length)] as WorkflowAction
      const outcome = outcomeFor(seed, key)
      entries.push({
        id: key,
        workflowId: workflow.id,
        workflowName: workflow.name,
        at,
        outcome,
        summary: OUTCOME_SUMMARY[outcome](action, workflow.trigger.detail),
        entity: subject.entity,
        drillDown: { path: subject.path, ...(subject.entity ? { entity: subject.entity } : {}) },
      })
    }
  }
  return entries.sort((a, b) => b.at.localeCompare(a.at) || a.id.localeCompare(b.id))
}
